import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { listResource } from '@/lib/api';
import { studentProfilePath } from '@/lib/user-detail-navigation';

interface AcademicRecord {
  id: string;
  userId?: string;
  coursesEnrolled?: number;
  coursesPassed?: number;
  averageGrade?: number | null;
  totalAttempts?: number;
  user?: {
    id: string;
    fullName?: string;
    name: string;
    email: string;
    avatarUrl?: string;
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

export function ExpedientePage() {
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const canViewProfile = hasAnyPermission(PERMISSIONS.USUARIOS_VER, PERMISSIONS.ESTUDIANTES_PERFIL_VER);
  const profileFrom = `${location.pathname}${location.search}`;
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch();
  const appliedSearch = debounced.trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['academic-records', appliedSearch, page],
    queryFn: () =>
      listResource<AcademicRecord>('/academic-records', {
        page,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const records = data?.data ?? [];
  const meta = data?.meta;
  const perPage = meta?.perPage ?? 30;
  const total = meta?.total ?? records.length;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  const hasSearch = search.trim().length > 0;

  return (
    <div className="lms-academic-records lms-site-admin-hub-page font-sans">
      <header className="flex items-start gap-4">
        <HubBackLink to="/administracion?tab=grades" label="Volver a Administración" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Expedientes Académicos</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Vista administrativa de registros académicos
          </p>
        </div>
      </header>

      <section className="lms-academic-records__toolbar">
        <form method="get" action="/expediente/admin" className="lms-academic-records__toolbar-form" onSubmit={(event) => event.preventDefault()}>
          <SearchInput
            name="search"
            value={search}
            onChange={setSearch}
            placeholder="Buscar estudiante"
            aria-label="Buscar estudiante"
            className="lms-academic-records__search"
          />
          {hasSearch && (
            <div className="lms-academic-records__toolbar-actions">
              <Link
                to="/expediente/admin"
                className="lms-academic-records__clear-btn"
                onClick={() => {
                  setSearch('');
                }}
              >
                Limpiar
              </Link>
            </div>
          )}
        </form>
      </section>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : records.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    folder_open
                  </span>
                </div>
                <h3>Sin registros</h3>
                <p>
                  {hasSearch
                    ? 'No se encontraron expedientes con la búsqueda aplicada.'
                    : 'No hay expedientes académicos registrados.'}
                </p>
                {hasSearch ? (
                  <button
                    type="button"
                    className="lms-gradebook__clear"
                    onClick={() => {
                      setSearch('');
                    }}
                  >
                    Restablecer filtros
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Estudiante
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Inscritos
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Aprobados
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Promedio
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Intentos
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      const name = record.user?.fullName ?? record.user?.name ?? 'Usuario';
                      const average = record.averageGrade;
                      const userId = record.user?.id ?? record.userId;
                      const profileTo = canViewProfile && userId ? studentProfilePath(userId, profileFrom) : null;
                      const nameTo = record.user && userId ? `/expediente/admin/${userId}` : profileTo;

                      return (
                        <tr key={record.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              {record.user?.avatarUrl ? (
                                <img src={record.user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                              ) : (
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {initials(name) || '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                {nameTo ? (
                                  <Link to={nameTo} className="lms-gradebook__name">
                                    {name}
                                  </Link>
                                ) : (
                                  <span className="lms-gradebook__name">{name}</span>
                                )}
                                <div className="lms-gradebook__muted">{record.user?.email ?? '—'}</div>
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">{record.coursesEnrolled ?? 0}</td>
                          <td className="lms-gradebook__td">{record.coursesPassed ?? 0}</td>
                          <td className="lms-gradebook__td">
                            {average != null ? (
                              <span className="lms-gradebook__emphasis">{Number(average).toFixed(1)}%</span>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                          <td className="lms-gradebook__td">{record.totalAttempts ?? 0}</td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            {nameTo || profileTo ? (
                              <div className="flex items-center justify-end gap-1">
                                {record.user && userId ? (
                                  <Link
                                    to={`/expediente/admin/${userId}`}
                                    className="lms-gradebook__action"
                                    title="Ver expediente"
                                    aria-label={`Ver expediente de ${name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      visibility
                                    </span>
                                  </Link>
                                ) : null}
                                {profileTo ? (
                                  <Link
                                    to={profileTo}
                                    className="lms-gradebook__action"
                                    title="Ver perfil"
                                    aria-label={`Ver perfil de ${name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      person
                                    </span>
                                  </Link>
                                ) : null}
                              </div>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {total > 0 && (
                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de <strong>{total}</strong>{' '}
                    resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{page}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={page >= lastPage}
                      onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
